import React, { useEffect, useState } from 'react';
import PlatformOverview from '../components/admin/PlatformOverview';
import Icon from '../components/ui/Icon';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../services/api';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const getTabFromQuery = (search) => {
    const tab = new URLSearchParams(search).get('tab');
    if (tab === 'colleges') return 'colleges';
    if (tab === 'companies') return 'companies';
    return 'stats';
  };

  const [activeTab, setActiveTab] = useState(getTabFromQuery(location.search));
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newCollege, setNewCollege] = useState({
    name: '', code: '', contact_email: '',
    adminName: '', adminEmail: '', adminPassword: ''
  });
  const [companies, setCompanies] = useState([]);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '', pattern_description: '', difficulty_level: 'medium'
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    setActiveTab(getTabFromQuery(location.search));
  }, [location]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'colleges') {
        const res = await API.get('/colleges');
        setColleges(res.data);
      } else if (activeTab === 'companies') {
        const res = await API.get('/companies/admin');
        setCompanies(res.data);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCollege = async (e) => {
    e.preventDefault();
    try {
      await API.post('/colleges', newCollege);
      setShowModal(false);
      setNewCollege({ name: '', code: '', contact_email: '', adminName: '', adminEmail: '', adminPassword: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Error creating college');
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    try {
      await API.post('/companies', newCompany);
      setShowCompanyModal(false);
      setNewCompany({ name: '', pattern_description: '', difficulty_level: 'medium' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Error creating company');
    }
  };

  const toggleCompanyStatus = async (id, currentStatus) => {
    try {
      await API.put(`/companies/${id}`, { is_active: !currentStatus });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleCollegeStatus = async (id, currentStatus) => {
    try {
      await API.put(`/colleges/${id}`, { is_active: !currentStatus });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="super-admin-page">
      <div className="super-header">
        <div>
          <h1>{activeTab === 'colleges' ? 'Colleges' : activeTab === 'companies' ? 'Companies' : 'Platform overview'}</h1>
          <p>{activeTab === 'stats' ? 'Live aggregates across every college on Zaptude' : 'Platform-level management'}</p>
        </div>
      </div>

      {activeTab === 'stats' && <PlatformOverview />}

      {activeTab === 'colleges' && (
        <div className="colleges-view">
          <div className="view-header">
            <h3>Registered Institutions <span className="view-count-pill">{colleges.length}</span></h3>
            <button className="add-college-btn" onClick={() => setShowModal(true)}>
              + Onboard New College
            </button>
          </div>

          <div className="college-list-card">
            {loading ? <div className="loader">Updating list...</div> : (
              <table className="super-table">
                <thead>
                  <tr>
                    <th>College Name</th>
                    <th>Subdomain</th>
                    <th>Users</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {colleges.map(college => (
                    <tr key={college.id}>
                      <td>
                        <div className="college-name-cell">
                          <span className="college-avatar">{college.name ? college.name.charAt(0).toUpperCase() : '?'}</span>
                          <span className="college-name-text">{college.name}</span>
                        </div>
                      </td>
                      <td><span className="subdomain-chip">{college.code.toLowerCase()}</span></td>
                      <td><span className="users-pill"><Icon name="users" size={13} /> {college.users?.[0]?.count || 0}</span></td>
                      <td>
                        <span className={`status-badge ${college.is_active ? 'online' : 'offline'}`}>
                          {college.is_active ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="action-btn"
                          onClick={() => navigate(`/admin/batches-overview?college_id=${college.id}`)}
                        >
                          View Analytics
                        </button>
                        <button
                          className={`action-btn ${college.is_active ? 'danger' : 'success'}`}
                          onClick={() => toggleCollegeStatus(college.id, college.is_active)}
                        >
                          {college.is_active ? 'Suspend' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'companies' && (
        <div className="companies-view">
          <div className="view-header">
            <h3>Global Institutions List</h3>
            <button className="add-college-btn" onClick={() => setShowCompanyModal(true)}>
              + Add New Company
            </button>
          </div>

          <div className="college-list-card">
            {loading ? <div className="loader">Updating companies...</div> : (
              <table className="super-table">
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Pattern Info</th>
                    <th>Difficulty</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map(company => (
                    <tr key={company.id}>
                      <td className="bold">{company.name}</td>
                      <td>
                         <div className="truncate-text" title={company.pattern_description}>
                            {company.pattern_description || 'No description added'}
                         </div>
                      </td>
                      <td>
                        <span className={`diff-badge sm ${company.difficulty_level}`}>
                          {company.difficulty_level}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${company.is_active ? 'online' : 'offline'}`}>
                          {company.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="action-btn toggle"
                          onClick={() => toggleCompanyStatus(company.id, company.is_active)}
                        >
                          {company.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="super-modal">
            <div className="modal-header">
              <h2>Onboard New College</h2>
              <button className="close-modal" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddCollege}>
              <div className="form-section">
                <h4>Institutional Details</h4>
                <div className="form-row">
                  <input type="text" placeholder="College Name" required
                    value={newCollege.name} onChange={e => setNewCollege({...newCollege, name: e.target.value})} />
                  <input type="text" placeholder="Subdomain Name (e.g. npsb)" required
                    value={newCollege.code} onChange={e => setNewCollege({...newCollege, code: e.target.value})} />
                </div>
                <input type="email" placeholder="Official Contact Email" required
                  value={newCollege.contact_email} onChange={e => setNewCollege({...newCollege, contact_email: e.target.value})} />
              </div>

              <div className="form-section">
                <h4>Primary Admin Credentials</h4>
                <input type="text" placeholder="Admin Name" required
                   value={newCollege.adminName} onChange={e => setNewCollege({...newCollege, adminName: e.target.value})} />
                <div className="form-row">
                  <input type="email" placeholder="Admin Email" required
                     value={newCollege.adminEmail} onChange={e => setNewCollege({...newCollege, adminEmail: e.target.value})} />
                  <input type="password" placeholder="Temp Password" required
                     value={newCollege.adminPassword} onChange={e => setNewCollege({...newCollege, adminPassword: e.target.value})} />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="save-btn">Create Institution</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCompanyModal && (
        <div className="modal-overlay">
          <div className="super-modal">
            <div className="modal-header">
              <h2>Add New Platform Company</h2>
              <button className="close-modal" onClick={() => setShowCompanyModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddCompany}>
              <div className="form-section">
                <label>Company Name</label>
                <input type="text" placeholder="e.g. TCS, Infosys, Google" required
                  value={newCompany.name} onChange={e => setNewCompany({...newCompany, name: e.target.value})} />
                
                <label style={{ marginTop: '15px', display: 'block' }}>Pattern Description</label>
                <textarea rows={3} placeholder="Describe the exam pattern or recruitment process..."
                  value={newCompany.pattern_description} onChange={e => setNewCompany({...newCompany, pattern_description: e.target.value})} />

                <label style={{ marginTop: '15px', display: 'block' }}>Platform Difficulty Rating</label>
                <select value={newCompany.difficulty_level} onChange={e => setNewCompany({...newCompany, difficulty_level: e.target.value})}>
                   <option value="easy">Easy</option>
                   <option value="medium">Medium</option>
                   <option value="hard">Hard</option>
                </select>
              </div>

              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setShowCompanyModal(false)}>Cancel</button>
                <button type="submit" className="save-btn">Add Company</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
